// Book Writing service config (Post a Project, Atrium Stage 7).
//
// Source of truth = the Rails backend, NOT the design prototype:
//   whitelist  app/controllers/api/v1/project_postings_controller.rb (book_writing_posting_params)
//   views      app/views/job_postings/steps/book_writing/_one.._seven.html.erb
//   validation app/models/job_postings/job_postings/step_validations.rb (book_writing__one.._seven)
//   cost       app/models/job_postings/book_writing_posting.rb (#cost / #editing_cost)
//   parity     tasks/post-a-project/parity/book-writing-addons.md
//
// Field keys are the snake_case backend attrs. Composites (consent, triggerWarning)
// are expanded to backend booleans/JSON by serialize.js, not here. The Managed
// Services / QA / QC add-on is a SHARED job-level step (lib/post/addon.js), not
// part of this config.
//
// Restorations vs the design prototype:
//   - subtitle is optional
//   - real AI consent step (mode "3"); the design dropped the consent booleans
//   - real trigger-warning composite; Finance keeps an existing warning reachable
//   - Finance omits untouched warnings, but exposes already-enabled warnings so
//     their required details can be edited or the warning intentionally declined
//   - `images` is a BOOLEAN toggle (schema: images :boolean), NOT a priced number;
//     book_writing #cost has no image term. Shown only for Premium/Top/Finance and
//     Technology non-fiction (helper display_20_images_field?).

import {
  WRITING_TIERS,
  FICTION_WRITING_TIERS,
  ALL_TIER_VALUE,
  WRITING_STYLES,
  WRITING_GENRES,
  WRITING_EDITING_STYLES,
  RATES,
} from "../options"

// string[] -> [{ value, label }] for select / radio fields.
const asOptions = (arr) => arr.map((v) => ({ value: v, label: v }))

const money = (n) => Math.round(n * 100) / 100

const FINANCE_AND_TECH = "Finance and Technology"

// Tiers where book_writing offers the "include images" toggle (non-fiction only).
const IMAGE_TIERS = ["Premium", "Top", FINANCE_AND_TECH]

// Per-100-word writer rate for one tier (tier cards display only; rail is
// authoritative). Server-resolved, Insider-aware ctx.rates read per-key: a null
// server leaf for that tier falls back to the RATES seed for THAT tier, never to
// 0 (ruling C2 / tuw-app:P-001). Returns undefined for a tier absent from both
// maps, so callers' `!= null` guards still short-circuit (All-Tier / unknown).
export const writerRateFor = (tier, useAi, ctx = {}) =>
  useAi
    ? (ctx.rates?.ai_writer_cost?.[tier] ?? RATES.AI_WRITER_COST[tier])
    : (ctx.rates?.writer_cost?.[tier] ?? RATES.WRITER_COST[tier])

const rateText = (tier, useAi, ctx = {}) => {
  const rate = writerRateFor(tier, useAi, ctx)
  return rate != null ? `$${rate.toFixed(2)} / 100 words` : undefined
}

// All-Tier "From $X / 100 words" = the Rising floor rate (design rateFor uses the
// Rising rate for All-Tier), method-aware. Server-traced.
const allTierFromText = (useAi, ctx = {}) => {
  const rate = writerRateFor("Rising", useAi, ctx)
  return rate != null ? `From $${rate.toFixed(2)} / 100 words` : undefined
}

// Design tier descriptions (jobposting.js TIER_DESC). Em dashes converted per the
// no-em-dash rule.
const TIER_DESCRIPTIONS = {
  [ALL_TIER_VALUE]:
    "No tier restriction, see more requests and choose a writer on skill. You pay the rate of the tier your chosen writer sits in.",
  [FINANCE_AND_TECH]:
    "Subject-matter experts for Business, Money & Technology. Required for those niches. Includes up to 20 royalty-free images.",
  Premium:
    "Our highest-performing writers, consistently excellent. Non-fiction includes up to 20 royalty-free images; fiction, none.",
  Top: "Proven writers who produce consistent, high-quality work. Non-fiction includes up to 10 royalty-free images; fiction, none.",
  Urban: "Up-and-coming writers showing great potential and good quality. No images included.",
  Rising:
    "Vetted, trained writers just getting started, still learning, but writing professionally. No images included.",
}

// Design editing-style descriptions (jobposting.js EDITING_STYLES).
const WRITING_EDITING_DESCRIPTIONS = {
  "Essential Editing":
    "Included with every writing package. A review for grammar, spelling, syntax, punctuation and inconsistencies.",
  "Line & Essential Editing":
    "Everything in Essential, plus a line-by-line review for clarity, coherence, flow, phrasing, word choice, transitions, sentence variety and redundancy.",
}

// Design genre order (jobposting.js WRITING_GENRES): alphabetical with
// "Other (Fiction)" pinned LAST, rather than a plain `.sort()` that buries it
// mid-list.
const GENRE_OPTIONS = [...WRITING_GENRES]
  .filter((g) => g !== "Other (Fiction)")
  .sort()
  .concat("Other (Fiction)")

// editing add — Ruby book_writing #editing_cost = (word_count / 100) * rate, where
// `word_count / 100` is INTEGER division (floor). rate is the per-100-word dollar
// value (Essential 0 / Line & Essential 0.8). Server-traced via ctx.rates.
const editingRateFor = (style, ctx = {}) =>
  ctx.rates?.writing_editing_styles_cost?.[style] ?? RATES.WRITING_EDITING_STYLES_COST[style] ?? 0

const BYO_OUTLINE_SUB = "Already have an outline? Bring your own and your writer will work from it."

// The legacy bundle outline choice (book_writing/_three.html.erb, bundle branch),
// labels verbatim. The upgrade's "Starting at" amount and description are the
// Platinum bundle card's own fromPrice/header (ctx.upgradeBundle, from the
// catalog), so the choice quotes the same price the bundle picker shows.
const bundleOutlineOptions = (ctx = {}) => {
  const platinum = ctx.upgradeBundle || {}
  const amount = platinum.fromPrice?.amount

  return [
    {
      value: true,
      label: "Upgrade To Our Platinum Bundle",
      price: amount ? `Starting at ${amount}` : undefined,
      sub: platinum.header,
    },
    {
      value: false,
      label: "Bring Your Own Outline",
      price: "Included",
      sub: BYO_OUTLINE_SUB,
    },
  ]
}

export const bookWriting = {
  key: "book_writing",
  postingKey: "book_writing_posting",
  name: "Book Writing",

  // Design defaults (jobposting.js DEFAULTS). Seeded into the posting sub-state
  // when the service is entered so they become REAL submitted values, not a
  // display-only preselect — serialize only sends keys present in state, and the
  // backend requires editing_style (and writing_style on the fiction path).
  // writing_style only serializes on the fiction path (its `when` guard), so
  // seeding it is harmless for non-fiction.
  defaults: {
    writing_style: "First Person",
    editing_style: "Essential Editing",
  },

  steps: (values = {}) => {
    const fiction = !!values.fiction
    const useAi = !!values.use_ai

    return [
      // Step 1: What are you writing? (book_writing __one). Fiction vs non-fiction is
      // a guided radio choice-step, not a toggle: the two paths diverge from here
      // (tier list, style/genre vs niche, word-count floor). Hidden when a bundle
      // locks the manuscript to fiction (Ultimate Fiction, set at launch); serialize
      // then pins fiction = true even though this step never renders. `fiction` is a
      // boolean column, so the option values are real booleans. State, serialize.js,
      // bundle.js and every `!!v.fiction` reader stay boolean-correct (the renderer
      // maps Radix's string back to the configured boolean; isEmpty(false) is
      // false, so Non-fiction counts
      // as answered for the required check).
      {
        id: "format",
        node: "Format",
        title: "What are you writing?",
        sub: "Fiction and non-fiction follow slightly different paths from here.",
        when: (v, ctx) => !(ctx && ctx.bundleForceFiction),
        fields: [
          {
            // Design's free-to-post intro banner above the fiction cards
            // (post-project.html stageFormat). Em dash converted per the
            // no-em-dash rule; semantics unchanged.
            type: "notice",
            text: "We’ll walk you through a few quick choices to post your book project. It’s free to post, you only pay once you pick a writer to start. Nothing is locked in along the way.",
          },
          {
            // The inline outline step is hidden when the BYO selection already
            // contains a standalone Outline (AT-04) — explain the disappearance
            // where the Book Writing setup begins.
            type: "notice",
            text: "Your outline is handled by the Outline package in this project.",
            when: (v, ctx) => !(ctx && ctx.bundle) && (ctx?.selected || []).includes("outline"),
          },
          {
            type: "radio",
            key: "fiction",
            reviewLabel: "Book type",
            required: true,
            options: [
              {
                value: true,
                label: "Fiction",
                sub: "Novels, short stories, and series built around characters, plot, and imaginative storytelling.",
              },
              {
                value: false,
                label: "Non-fiction",
                sub: "Guides, memoirs, business books, and other fact-based work built around real ideas and expertise.",
              },
            ],
          },
        ],
      },

      // Step 2: Human-written vs AI-generated manuscript (book_writing __two).
      // Non-fiction ONLY (fiction is always human; use_ai stays false there) and only
      // when the customer can post AI work (canUseAi). `use_ai` is a boolean column.
      {
        id: "manuscript",
        node: "Manuscript",
        title: "How should it be written?",
        sub: "Both work with a vetted writer and editor. AI-assisted is faster and lower cost.",
        when: (v, ctx) => v.fiction === false && !!(ctx && ctx.canUseAi),
        fields: [
          {
            type: "radio",
            key: "use_ai",
            reviewLabel: "Manuscript",
            required: true,
            // "Includes" deliverable lists per option (design post-project.html
            // stageManuscript). Em dashes converted per the no-em-dash rule.
            options: [
              {
                value: false,
                label: "Human-written manuscript",
                sub: "Work with a professional freelancer to craft unique content for an engaging read. No AI is used unless you consent, and consent never includes draft generation. Content is completely human-created.",
                includes: [
                  "Full manuscript, from 1,000 words",
                  "Professional editing & research",
                  "Plagiarism scan report",
                  "Drafts & revisions",
                ],
              },
              {
                value: true,
                label: "AI-generated manuscript",
                price: "Lower cost",
                sub: "Combine AI and human intelligence. A freelance writer works closely with custom AI tools to build out your book, then a professional editor gives it a final review. Minimum 10,000 words.",
                includes: [
                  "Book outline",
                  "Full manuscript from 10,000 words, writing + editing",
                  "Plagiarism scan report",
                  "Drafts & revisions",
                  "Professional book description",
                ],
              },
            ],
          },
        ],
      },

      // Step 3: Outline (book_writing __three). Guided radio choice-step. Skipped for
      // AI manuscripts (the AI path bundles its own outline), hidden inside a
      // bundle that includes an outline (Platinum/Fiction/Narration — the inline one
      // would mint a duplicate outline_posting), and hidden when the BYO selection
      // already contains a standalone Outline — the customer already configured
      // one, so asking again would double-charge it (AT-04; the format step carries
      // the explanatory note). A Silver/Gold bundle gets the legacy bundle choice
      // (_three.html.erb): upgrade to Platinum, or bring your own outline. Picking
      // the upgrade switches the draft's bundle tier (PostProject setPosting); the
      // bundle serializer always pins add_outline false. `add_outline` is a boolean:
      // "Add an outline" (true) bills a separate outline_posting at submit (Plot for
      // fiction, Professional for non-fiction); "Bring your own" (false) does not.
      // "Bring your own" (false) also lets the customer upload their outline as the
      // `file` ActiveStorage attachment on book_writing_posting (whitelisted as `file`
      // in book_writing_posting_params; serialize.js carries the File through). This
      // mirrors live, which captures the customer's own outline on `file`. The `file`
      // key is shared with the details step's requirements upload, but the two are
      // mutually exclusive: outline `file` only renders on the non-AI path, details
      // `file` only on the AI path (use_ai), so they never collide on the same state.
      {
        id: "outline",
        node: "Outline",
        title: "Outline",
        sub: "A strong outline sets up a strong manuscript. Add one, or bring your own.",
        when: (v, ctx) => {
          if (v.use_ai) return false
          if (ctx && ctx.bundle) return !ctx.bundleIncludesOutline
          return !(ctx?.selected || []).includes("outline")
        },
        fields: [
          {
            type: "radio",
            key: "add_outline",
            reviewLabel: "Outline",
            required: true,
            // "Add an outline" bills a SEPARATE outline_posting at submit; the card
            // must show that REAL billed price (Plot $420 / Professional $300), not
            // the design's stale "Included"/"+$120" that contradicted the rail
            // (Plumbline bug 13, ruling C2). Prices derive from the customer's
            // PriceList via ctx.outlinePrices (Insider-aware), falling back to the
            // standard RATES so the card always shows a real number.
            options: (v = {}, ctx = {}) => {
              if (ctx && ctx.bundle) return bundleOutlineOptions(ctx)

              const rates = (ctx && ctx.outlinePrices) || {}
              const professional = rates.professional ?? RATES.OUTLINE_COST
              const plot = rates.plot ?? RATES.PLOT_OUTLINE_TOTAL
              return [
                v.fiction
                  ? {
                      value: true,
                      label: "Plot Outline",
                      price: `$${money(plot).toFixed(2)}`,
                      sub: "A truly collaborative process between a fiction writer, a developmental editor and yourself, ironing out the details needed to bring your story to life.",
                    }
                  : {
                      value: true,
                      label: "Professional Outline",
                      price: `$${money(professional).toFixed(2)}`,
                      sub: "The success of your book depends on a top-notch outline. Collaborate one-on-one with a professional outline writer, follow along and give feedback, or let them take care of it all.",
                    },
                {
                  value: false,
                  label: "Bring your own outline",
                  price: "Included",
                  sub: BYO_OUTLINE_SUB,
                },
              ]
            },
          },
          {
            type: "file",
            key: "file",
            label: "Upload your outline",
            // REQUIRED when bringing your own outline: step_validations.rb
            // book_writing__three enforces the upload for add_outline == false
            // (Plumbline bug 6, ruling C3). The field only renders in that state.
            required: (v) => v.add_outline === false,
            accept: ".doc,.docx,.pdf,.zip",
            placeholder: "Upload a file",
            when: (v) => v.add_outline === false,
          },
        ],
      },

      // Step 2 — Writer tier (book_writing __four). Tier list narrows to the
      // fiction set when fiction OR use_ai (helper writing_tiers(fiction || use_ai)).
      {
        id: "tier",
        node: "Writer tier",
        title: "Choose a writing tier",
        sub: "Tiers reflect a writer’s track record on Dibbly. You’ll see the per-100-word rate for each.",
        fields: [
          {
            type: "radio",
            key: "tier",
            label: "Writer tier",
            required: true,
            options: (v = {}, ctx = {}) => {
              const list = v.fiction || v.use_ai ? FICTION_WRITING_TIERS : WRITING_TIERS
              return list.map((tier) => ({
                value: tier,
                label: tier === ALL_TIER_VALUE ? "All Tier Access" : tier,
                price:
                  tier === ALL_TIER_VALUE
                    ? allTierFromText(!!v.use_ai, ctx)
                    : rateText(tier, !!v.use_ai, ctx),
                sub: TIER_DESCRIPTIONS[tier],
              }))
            },
          },
        ],
      },

      // Step 3 — Book details (book_writing __five).
      {
        id: "details",
        node: "Book details",
        title: "Book details",
        sub: "Tell your writer about the book. You can refine all of this with them later.",
        fields: [
          // Word count — min 1,000 normally, min 10,000 when use_ai OR in a bundle
          // (BUNDLE_MIN_WORD_COUNT). FieldRenderer reads `min`/`step` statically, so
          // split by `when`.
          {
            type: "wordcount",
            key: "word_count",
            label: "Word count",
            required: true,
            min: 1000,
            step: 500,
            when: (v, ctx) => !v.use_ai && !(ctx && ctx.bundle),
          },
          {
            type: "wordcount",
            key: "word_count",
            label: "Word count",
            required: true,
            min: 10000,
            step: 500,
            help: "Bundles and AI-generated manuscripts start at 10,000 words.",
            when: (v, ctx) => !!v.use_ai || !!(ctx && ctx.bundle),
          },
          {
            type: "select",
            key: "writing_style",
            label: "Writing style",
            required: true,
            blankLabel: "Select writing style",
            options: asOptions(WRITING_STYLES),
            when: (v) => !!v.fiction,
          },
          {
            type: "select",
            key: "genre",
            label: "Genre",
            required: true,
            blankLabel: "Select genre",
            options: asOptions(GENRE_OPTIONS),
            when: (v) => !!v.fiction,
          },
          {
            type: "select",
            key: "niche",
            label: "Niche / genre",
            required: true,
            blankLabel: "Select Niche/Genre",
            // Tier-scoped niches (niches_by_tier). options is a (values, ctx) fn.
            options: (v = {}, ctx = {}) =>
              asOptions((ctx.options && ctx.options.nichesForTier(v.tier)) || []),
            when: (v) => !v.fiction,
          },
          { type: "text", key: "title", label: "Title", required: true, placeholder: "e.g. The Quiet Investor" },
          { type: "text", key: "subtitle", label: "Subtitle", required: false },
          { type: "text", key: "author_name", label: "Author name", required: true, placeholder: "As it should appear on the book" },
          {
            type: "textarea",
            key: "author_bio",
            label: "Author bio",
            required: false,
            rows: 3,
            when: (v) => !v.fiction,
          },
          {
            type: "toggle",
            key: "add_glossary",
            label: "Add a glossary",
            when: (v) => !v.fiction,
          },
          {
            type: "toggle",
            key: "images",
            label: "Include images",
            help: "Premium and Finance and Technology include up to 20 images; Top includes up to 10. Extra images beyond the limit can be purchased at cost.",
            when: (v) => !v.fiction && IMAGE_TIERS.includes(v.tier),
          },
          {
            type: "toggle",
            key: "nsfw",
            label: "Topic is not safe for work",
            detailsKey: "nsfw_details",
            detailsLabel: "Reason topic is not safe for work",
            detailsType: "textarea",
            detailsRequired: true,
            when: (v) => v.tier !== FINANCE_AND_TECH || !!v.nsfw,
          },
          {
            type: "triggerWarning",
            label: "This book contains sensitive or triggering content",
            help: "Flag anything a writer should be aware of before taking the project.",
            when: (v) => v.tier !== FINANCE_AND_TECH || !!v.trigger_warning,
          },
          {
            type: "file",
            key: "file",
            label: "Requirements file",
            required: false,
            accept: ".doc,.docx,.pdf,.zip",
            placeholder: "Upload a file",
            when: (v) => !!v.use_ai,
          },
        ],
      },

      // Step 4 — Editing style (book_writing __six).
      {
        id: "editing",
        node: "Editing",
        title: "Editing style",
        sub: "Editing is included with every writing package. Choose the level for your project.",
        fields: [
          {
            type: "radio",
            key: "editing_style",
            reviewLabel: "Editing style",
            required: true,
            options: (v = {}, ctx = {}) =>
              WRITING_EDITING_STYLES.map((style) => {
                const rate = editingRateFor(style, ctx)
                return {
                  value: style,
                  label: style,
                  price: rate > 0 ? `$${(rate * 10).toFixed(2)} / 1,000 words` : "Included",
                  sub: WRITING_EDITING_DESCRIPTIONS[style],
                }
              }),
          },
        ],
      },

      // Step 5 — AI consent (book_writing __seven; mode "3": none / research / all).
      // SKIPPED on the AI path: live omits this step when use_ai is true and zeroes
      // all ai_consent_* flags (job_posting.rb). With the step hidden, serialize.js
      // visibleFields drops it so expandConsent never runs and no ai_consent_* keys
      // are sent — they default false in the DB, matching live.
      {
        id: "consent",
        node: "AI use",
        title: "AI use in your project",
        sub: "Dibbly is always transparent about AI. Choose how much your freelancer may use it, please select one option.",
        when: (v) => !v.use_ai,
        fields: [
          {
            type: "consent",
            key: "ai_consent",
            mode: "3",
            label: "AI use in your project",
            required: true,
          },
        ],
      },
    ]
  },

  // Instant estimate for the PriceRail. Ruby book_writing #cost (minus the shared
  // QA/QC/managed add-on, which the addon module contributes separately):
  //   (word_count / 100.0) * writer_rate          [exact float]
  //   + (word_count / 100)  * editing_rate         [integer floor]
  // All-tier access shows a "from" range (Ruby cost_as_text uses Rising..Premium).
  estimate: (values = {}, ctx = {}) => {
    const empty = { lines: [], total: 0, isRange: false, framing: "free-to-post" }

    const wc = Number(values.word_count) || 0
    const tier = values.tier
    if (!tier || wc <= 0) return empty

    const useAi = !!values.use_ai
    const allTier = tier === ALL_TIER_VALUE
    const rateTier = allTier ? "Rising" : tier
    const writerRate = writerRateFor(rateTier, useAi, ctx)
    if (writerRate == null) return empty

    const writerRaw = (wc / 100) * writerRate
    const editingRaw = Math.floor(wc / 100) * editingRateFor(values.editing_style, ctx)

    const lines = [
      {
        label: allTier
          ? `Book writing (${wc.toLocaleString()} words, all tiers)`
          : `Book writing (${wc.toLocaleString()} words, ${tier}${useAi ? ", AI" : ""})`,
        amount: money(writerRaw),
      },
    ]

    if (editingRaw > 0) {
      lines.push({ label: values.editing_style, amount: money(editingRaw) })
    }

    // "Add an outline" bills a separate outline_posting at submit. Mirror it as a
    // priced line so the rail matches the server estimate. Fiction = Plot Outline
    // ($420 = 300 + 120), non-fiction = Professional Outline ($300). The server is
    // authoritative; this uses the RATES table like every other service estimate.
    // When the BYO selection contains a standalone Outline, that service's own
    // estimate is the outline line — mirroring it here would show it twice
    // (AT-04; the server estimate skips its synthetic line the same way).
    const standaloneOutline = (ctx.selected || []).includes("outline")
    const outlineRaw = !standaloneOutline && values.add_outline
      ? values.fiction
        ? (ctx.outlinePrices?.plot ?? RATES.PLOT_OUTLINE_TOTAL)
        : (ctx.outlinePrices?.professional ?? RATES.OUTLINE_COST)
      : 0

    if (outlineRaw > 0) {
      lines.push({
        label: values.fiction ? "Plot Outline" : "Professional Outline",
        amount: money(outlineRaw),
      })
    }

    return {
      lines,
      total: money(writerRaw + editingRaw + outlineRaw),
      isRange: allTier,
      fromLabel: "Starting at",
      framing: "free-to-post",
    }
  },

  // /post/price entry (camelCase; underscored server-side). cost_fields for
  // book_writing_posting = [tier, editing_style, word_count, quality_assurance,
  // quality_control, use_ai]. QA/QC are JOB-LEVEL singletons the engine merges via
  // addon.addonPriceFields(state) — see report. Null until tier + word count set.
  pricePayload: (values = {}) => {
    const wc = Number(values.word_count) || 0
    if (!values.tier || wc <= 0) return null

    return {
      postingType: "book_writing_posting",
      tier: values.tier,
      editingStyle: values.editing_style ?? null,
      wordCount: wc,
      useAi: !!values.use_ai,
      // The "Add an outline" toggle bills a SEPARATE outline_posting at submit
      // (fiction → Plot Outline, non-fiction → Professional Outline). Send the
      // toggle + fiction flag so the server's #price action can mirror that
      // outline as a priced line in the estimate.
      addOutline: !!values.add_outline,
      fiction: !!values.fiction,
    }
  },
}
